import { EventEmitter, Injectable } from '@angular/core';
import { AuthService } from '@auth';
import { SecurityService } from './security.service';
import { environment } from 'src/environments/environment';
import { AppSettingsService } from './app-settings.service';
import { FirestoreService } from './firestore.service';
import { PullDataService } from './pull-data.service';
import { PushDataService } from './push-data.service';
import { Guid } from 'guid-typescript';
import { OnlineOfflineService, SystemSettingsService } from '@core';
import { debounceTime } from 'rxjs';
import { ModuleActivationService } from './module-activation.service';
import { PreferredOrganizationService } from './preferred-organization.service';
import { UnsyncedRecordsService } from './unsynced-records.service';
import { BaseReplicableService } from './base-replicable.service';
import { RecoveryService } from './replication/recovery.service';
import { CashTransactionService } from './cash-transaction.service';
import { CashRegisterStatusLogReplicationService } from './cash-register-status-log-replication.service';
import { SalesOrderV3ReplicationService } from './sales-order-v3-replication.service';
import { SalesOrderReplicationService } from './sales-order-replication.service';

@Injectable({
  providedIn: 'root'
})
export class SyncService {

  syncStatusChanged: EventEmitter<boolean>;

  /** Timestamp (ms) when the connection last dropped, or null while online. */
  private offlineSince: number | null = null;
  /** Minimum offline duration before a reconnect triggers recovery. */
  private static readonly RECOVERY_OFFLINE_DEBOUNCE_MS = 3000;

  constructor(
    private authService: AuthService,
    private pullDataService: PullDataService,
    private pushDataService: PushDataService,
    private securityService: SecurityService,
    private firestoreService: FirestoreService,
    private onlineOfflineService: OnlineOfflineService,
    private moduleActivationService: ModuleActivationService,
    private systemSettingsService: SystemSettingsService,
    private preferredOrganizationService: PreferredOrganizationService,
    private unsyncedRecordsService: UnsyncedRecordsService,
    private recoveryService: RecoveryService,
    // Injected so these services self-register in the EntityRegistry at app
    // start, making their entities discoverable by RecoveryService even before
    // the first cash movement / order is created.
    private cashTransactionService: CashTransactionService,
    private cashRegisterStatusLogReplicationService: CashRegisterStatusLogReplicationService,
    private salesOrderV3ReplicationService: SalesOrderV3ReplicationService,
    private salesOrderReplicationService: SalesOrderReplicationService,
  ) {
    this.syncStatusChanged = pullDataService.syncStatusChanged;

    // Sync data to indexeddb on login
    this.authService.userLoggedIn$.subscribe({
      next: () => {
        // US 27702: the sync cycle belongs to the preferred organization only.
        if (!this.isPreferredTenantActive()) return;

        this.pullDataService.requestSync();
        this.pushDataService.pushLocalUpdatesToServer();
        // Generic recovery: restore replicated entities from the Desktop back into Firestore/IndexedDB.
        this.recoveryService.restoreAllReplicatedData();
      }
    });

    this.onlineOfflineService.connectionChanged.subscribe({
      next: () => {
        if (!this.onlineOfflineService.isOnline) {
          if (this.offlineSince === null) {
            this.offlineSince = Date.now();
          }
          return;
        }

        if (!this.authService.isAuthenticated()) {
          this.offlineSince = null;
          return;
        }

        const offlineDuration =
          this.offlineSince === null ? 0 : Date.now() - this.offlineSince;
        this.offlineSince = null;

        // Debounced: only run generic recovery after a real outage
        // (3+ seconds offline), not on every brief connectivity blip.
        if (offlineDuration >= SyncService.RECOVERY_OFFLINE_DEBOUNCE_MS && this.isPreferredTenantActive()) {
          this.recoveryService.restoreAllReplicatedData();
        }
      }
    });

    // Schedule sync to run every X minutes
    if (environment.production) {
      setInterval(() => {
        // US 27702: evaluated per tick — the user may have switched to a
        // server-mode tenant (or back) since the previous interval.
        if (!this.isPreferredTenantActive()) return;

        this.pullDataService.requestPeriodicSync();
        this.pushDataService.pushLocalUpdatesToServer();
      }, AppSettingsService.appSettings.offlineMode.syncIntervalMs);
    }

    if (`serviceWorker` in navigator) {
      navigator.serviceWorker.addEventListener('message', async event => this.authHeadersEventHandler(event));
    }

    // US 27702: keep the persisted pending-records flag fresh so another user's
    // session can honor this browser's unsynced records after a user change.
    this.syncStatusChanged.subscribe({
      next: (completed: boolean) => {
        if (completed) void this.unsyncedRecordsService.refreshPersistedStateAsync();
      }
    });
    // 10s debounce: each refresh fans out ~10 Firestore/Dexie count queries, so a
    // busy POS shift must not re-run it per sale — the flag only has to be fresh
    // by the time a DIFFERENT user signs in.
    BaseReplicableService.localWriteCompleted$.pipe(debounceTime(10000)).subscribe({
      next: () => void this.unsyncedRecordsService.refreshPersistedStateAsync()
    });
    this.authService.userLoggedIn$.subscribe({
      next: () => void this.unsyncedRecordsService.refreshPersistedStateAsync()
    });
    this.authService.tenantSwitched$.subscribe({
      next: () => void this.unsyncedRecordsService.refreshPersistedStateAsync()
    });

    // US 27702: the moment an organization becomes preferred (auto-single pick or
    // pre-feature migration) its data must start downloading — the login-time sync
    // above may already have run and been gated off.
    this.preferredOrganizationService.preferredChanged$.subscribe({
      next: () => {
        if (!this.isPreferredTenantActive()) return;
        this.pullDataService.requestSync();
        this.pushDataService.pushLocalUpdatesToServer();
      }
    });
  }

  pushLocalUpdatesToServer() {
    this.pushDataService.pushLocalUpdatesToServer();
  }

  private isPreferredTenantActive(): boolean {
    return this.preferredOrganizationService.isPreferredTenant(this.authService.currentTenant);
  }

  private async authHeadersEventHandler(event: any) {
    if (event.data.message === 'requestingAuthHeaders') {
      if (!this.authService.isAuthenticated()) {
        return;
      }

      // US 27702: without this reply the service worker never starts a sync pass —
      // its sync handler only posts 'requestingAuthHeaders' and waits. Server-mode
      // tenants therefore get no background pull/push at all.
      if (!this.isPreferredTenantActive()) {
        return;
      }

      const isFireStoreLoggedIn = await this.firestoreService.isLoggedIn();
      if (!isFireStoreLoggedIn) {
        await this.authService.renewFirebaseToken();
      }

      const firestoreIdToken = await this.firestoreService.getIdToken();
      const isPointOfSaleV3Enabled = await this.moduleActivationService.isPointOfSaleV3Enabled();
      const allowSellingBySerialNumber = await this.systemSettingsService.allowSellingBySerialNumber();
      const isDataReplicationEnabled = await this.systemSettingsService.isDataReplicationEnabled();
      const msg = {
        syncBatchId: Guid.create().toString(),
        accessToken: this.authService.accessToken,
        tenantId: this.authService.currentTenant,
        securityKey: this.securityService.key,
        firestoreIdToken: firestoreIdToken,
        userContext: {
          id: this.authService.currentUserId,
          email: this.authService.currentUserEmail,
          username: this.authService.currentUserEmail
        },
        environmentContext: {
          appVersion: environment.version,
          organizationId: this.authService.currentTenant,
          organizationName: this.authService.currentOrganization?.name
        },
        isPointOfSaleV3Enabled: isPointOfSaleV3Enabled,
        allowSellingBySerialNumber: allowSellingBySerialNumber,
        isDataReplicationEnabled: isDataReplicationEnabled
      };

      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          'command': 'authHeaders',
          'message': msg,
          'source': event.data.source
        });
      }
    }
  }

}
